import { useCallback, useRef, useState } from 'react';

type IdleState = { status: 'idle' };
type LoadingState = { status: 'loading' };
type ErrorState = { status: 'error'; error: string };
type SuccessState<T> = { status: 'success'; data: T };

export type QueryState<T> = IdleState | LoadingState | ErrorState | SuccessState<T>;

/**
 * A wrapper to track the state of a request e.g. loading, error, success
 */
export default function useQueryState<T>(fetcher: () => Promise<T>) {
	const [state, setState] = useState<QueryState<T>>({ status: 'idle' });
	const requestIdRef = useRef(0);

	// Query data by key
	const query = useCallback(async () => {
		const currentId = ++requestIdRef.current;
		setState({ status: 'loading' });
		try {
			const result = await fetcher();
			if (currentId === requestIdRef.current) {
				setState({ status: 'success', data: result });
			}
		} catch (err) {
			if (currentId === requestIdRef.current) {
				setState({ status: 'error', error: (err as Error).message });
				if (process.env.NODE_ENV === 'development') {
					console.error('Failed to query', (err as Error).message);
				}
			}
		}
	}, [fetcher]);

	return {
		state,
		query,
	};
}
